// Iter 90l — Shared pill-style tab bar so every app's top / sub navigation
// matches Settings' pattern: rounded-full pills with icons, primary-tinted
// active state, and an optional count badge.
//
// Iter 91d — supports optional drag-drop reorder for Super Admin. Pass
// `editable=true` + `onReorder(newTabs)` and the bar becomes draggable.
//
// Two exports:
//   • <PillTabBar> — the "top-level" style used by Settings' group row
//     (bigger pills, filled primary background when active, count badge).
//   • <SubPillTabBar> — the "sub-tab" style (smaller, white pill on the
//     slate strip, primary text when active).
import { useMemo, useRef, useState } from "react";

const cls = (...s) => s.filter(Boolean).join(" ");

export function PillTabBar({
  tabs, activeKey, onChange, testidPrefix = "pill-tab",
  showCounts = false, className = "",
  editable = false, onReorder = null,
}) {
  const dragRef = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const reorder = (targetKey) => {
    const srcKey = dragRef.current;
    if (!onReorder || !srcKey || srcKey === targetKey) return;
    const next = [...tabs];
    const from = next.findIndex((t) => t.key === srcKey);
    const to   = next.findIndex((t) => t.key === targetKey);
    if (from < 0 || to < 0) return;
    next.splice(to, 0, next.splice(from, 1)[0]);
    onReorder(next);
    dragRef.current = null; setDragOver(null);
  };

  return (
    <div className={cls("flex flex-wrap gap-1.5", className)} role="tablist">
      {tabs.map((t) => {
        const active = t.key === activeKey;
        const Icon = t.icon;
        const count = showCounts ? t.count : null;
        const draggable = editable && !!onReorder;
        return (
          <button key={t.key} type="button" role="tab"
                  aria-selected={active}
                  draggable={draggable}
                  onDragStart={draggable ? (e) => {
                    dragRef.current = t.key;
                    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", t.key); } catch { /* noop */ }
                  } : undefined}
                  onDragOver={draggable ? (e) => { e.preventDefault(); setDragOver(t.key); } : undefined}
                  onDragLeave={draggable ? () => setDragOver((p) => (p === t.key ? null : p)) : undefined}
                  onDrop={draggable ? () => reorder(t.key) : undefined}
                  onDragEnd={() => { dragRef.current = null; setDragOver(null); }}
                  onClick={editable ? undefined : () => onChange(t.key)}
                  data-testid={`${testidPrefix}-${t.key}`}
                  className={cls(
                    "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition",
                    active && !editable
                      ? "border-primary bg-primary text-primary-foreground shadow-sm"
                      : "border-slate-200 bg-white text-slate-600 hover:border-primary/40 hover:text-slate-900",
                    draggable ? "cursor-grab" : "",
                    dragOver === t.key ? "ring-2 ring-primary/50" : "",
                    t.className,
                  )}>
            {Icon ? <Icon className="h-4 w-4" /> : null}
            {t.label}
            {typeof count === "number" && (
              <span className={cls(
                "ml-1 rounded px-1.5 py-0.5 text-[10px] font-bold tabular-nums",
                active ? "bg-white/25" : "bg-slate-100 text-slate-500",
              )}>{count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function SubPillTabBar({
  tabs, activeKey, onChange, testidPrefix = "sub-pill",
  className = "",
}) {
  return (
    <div className={cls(
      "flex flex-wrap gap-1.5 rounded-lg bg-slate-50 p-2",
      className,
    )} role="tablist">
      {tabs.map((t) => {
        const active = t.key === activeKey;
        const Icon = t.icon;
        return (
          <button key={t.key} type="button" role="tab"
                  aria-selected={active}
                  onClick={() => onChange(t.key)}
                  data-testid={`${testidPrefix}-${t.key}`}
                  className={cls(
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition",
                    active
                      ? "border-primary bg-white text-primary shadow-sm"
                      : "border-transparent bg-white/70 text-slate-600 hover:text-slate-900",
                  )}>
            {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// Convenience for pages that want to split a flat list by category.
export function useGroupedTabs(items, keyOf, groupOf, labelOf, iconOf) {
  return useMemo(() => {
    const byGroup = {};
    for (const it of items) {
      const g = groupOf(it) || "default";
      (byGroup[g] = byGroup[g] || []).push({
        key:   keyOf(it),
        label: labelOf(it),
        icon:  iconOf?.(it),
        raw:   it,
      });
    }
    return byGroup;
  }, [items, keyOf, groupOf, labelOf, iconOf]);
}
